import { useMemo } from 'react';
import {
    normalizeAnswers,
    processGatekeeperData,
    processSignals,
    processConsoleLogs,
    categorizeAnswers,
    extractUrlData,
    processSiteTypeData,
    processCustomerFitData,
    processConsoleLogLevelSummary,
    processPerformanceMetrics,
    processDefaultMetricsDisplay,
    LOG_LEVEL_SUMMARY
} from '../utils/dataProcessors';
import { formatValue } from '../utils/formatters';

export const useWebsiteData = (websiteDetails) => {
    const website = websiteDetails?.website ?? null;
    const evaluation = website?.evaluation;
    const evaluationMessage = (website?.evaluation_message && String(website.evaluation_message).trim()) || null;
    const answers = website?.answers;
    const reasons = website?.reasons;
    const networkSummary = website?.network_summary;
    const rawConsoleLogs = website?.console_logs;
    const performanceMetrics = website?.performance_metrics;
    const mainUrl = website?.mainUrl;

    // Normalize answers
    const { normalizedAnswers, gatekeeperData: initialGatekeeperData } = normalizeAnswers(answers);
    
    // Process gatekeeper data
    const gatekeeperProcessed = processGatekeeperData(initialGatekeeperData, evaluation, website);
    const gatekeeperData = gatekeeperProcessed?.gatekeeperData || null;
    const gatekeeperShouldContinue = gatekeeperProcessed?.gatekeeperShouldContinue ?? null;
    const gatekeeperRejected = gatekeeperProcessed?.gatekeeperRejected ?? false;
    const gatekeeperConfidence = gatekeeperProcessed?.gatekeeperConfidence ?? null;
    const gatekeeperCategory = gatekeeperProcessed?.gatekeeperCategory ?? null;
    const gatekeeperReason = gatekeeperProcessed?.gatekeeperReason ?? null;

    // Gatekeeper status
    const gatekeeperStatus = useMemo(() => {
        if (gatekeeperShouldContinue === false) {
            return {
                label: 'Do Not Continue',
                color: '#f87171',
                background: 'linear-gradient(135deg, rgba(248, 113, 113, 0.22), rgba(248, 113, 113, 0.08))',
                border: 'rgba(248, 113, 113, 0.45)'
            };
        }

        if (gatekeeperShouldContinue === true) {
            return {
                label: 'Continue',
                color: '#34d399',
                background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.22), rgba(52, 211, 153, 0.08))',
                border: 'rgba(52, 211, 153, 0.45)'
            };
        }

        return {
            label: 'Needs Review',
            color: '#9ca3af',
            background: 'linear-gradient(135deg, rgba(156, 163, 175, 0.18), rgba(156, 163, 175, 0.06))',
            border: 'rgba(156, 163, 175, 0.35)'
        };
    }, [gatekeeperShouldContinue]);

    const showGatekeeperSummary = gatekeeperData && typeof gatekeeperData === 'object';
    const showTopGatekeeperSummary = Boolean(showGatekeeperSummary && (gatekeeperRejected || !evaluation));
    const showBottomGatekeeperStatusCard = Boolean(showGatekeeperSummary && !showTopGatekeeperSummary);

    // Process signals
    const gatekeeperSignalsRaw = gatekeeperData?.signals ?? null;
    const gatekeeperSignals = useMemo(() => {
        return processSignals(gatekeeperSignalsRaw);
    }, [gatekeeperSignalsRaw]);

    // Categorize answers
    const {
        standardAnswers,
        donationAnswers,
        dnsAnswers,
        seoAnswers,
        longTextAnswers
    } = categorizeAnswers(normalizedAnswers);

    // Extract URL data
    const urlData = useMemo(() => {
        return extractUrlData(normalizedAnswers);
    }, [normalizedAnswers]);

    // Process site type data
    const siteTypeData = processSiteTypeData(normalizedAnswers);

    // Process customer fit data
    const customerFitData = processCustomerFitData(normalizedAnswers);
    const customerFitSignals = useMemo(() => {
        return processSignals(customerFitData.customerFitSignalsRaw);
    }, [customerFitData.customerFitSignalsRaw]);

    // CSS data
    const cssData = website?.css_data || null;

    // Process console logs
    const consoleLogs = useMemo(() => {
        return processConsoleLogs(rawConsoleLogs);
    }, [rawConsoleLogs]);

    const consoleLogLevelSummary = useMemo(() => {
        return processConsoleLogLevelSummary(consoleLogs);
    }, [consoleLogs]);

    // Process performance metrics
    const metricsList = performanceMetrics?.performanceMetrics || [];
    const navigationTiming = performanceMetrics?.navigationTiming;
    const resourceEntries = performanceMetrics?.resourceEntries || [];
    const coreWebVitals = performanceMetrics?.coreWebVitals || null;
    const thirdPartySplit = performanceMetrics?.thirdPartySplit || null;
    const blockingScripts = performanceMetrics?.blockingScripts || [];
    const coverage = performanceMetrics?.coverage || null;

    const { defaultMetrics, navigationStartValue } = useMemo(() => {
        return processPerformanceMetrics(metricsList);
    }, [metricsList]);

    const defaultMetricsDisplay = useMemo(() => {
        return processDefaultMetricsDisplay(defaultMetrics, navigationStartValue, formatValue);
    }, [defaultMetrics, navigationStartValue]);

    const hasAdditionalMetrics = metricsList.length > defaultMetrics.length;

    // Network summary
    const initiatorEntries = networkSummary?.byInitiatorType
        ? Object.entries(networkSummary.byInitiatorType)
        : [];

    // Score visuals
    const parsedScore = useMemo(() => {
        const rawScore = evaluation?.score;

        if (rawScore === null || rawScore === undefined) {
            return null;
        }

        const numericScore = Number(rawScore);

        return Number.isFinite(numericScore) ? numericScore : null;
    }, [evaluation?.score]);

    const scoreVisuals = useMemo(() => {
        if (parsedScore === null) {
            return {
                accent: '#9ca3af',
                border: 'rgba(156, 163, 175, 0.45)',
                gradient: 'linear-gradient(135deg, rgba(156, 163, 175, 0.18), rgba(156, 163, 175, 0.06))',
                shadow: 'rgba(15, 23, 42, 0.2)',
                label: 'Awaiting score'
            };
        }

        if (parsedScore >= 8.5) {
            return {
                accent: '#34d399',
                border: 'rgba(52, 211, 153, 0.65)',
                gradient: 'linear-gradient(135deg, rgba(52, 211, 153, 0.24), rgba(52, 211, 153, 0.08))',
                shadow: 'rgba(52, 211, 153, 0.22)',
                label: 'Excellent performance'
            };
        }

        if (parsedScore >= 6.5) {
            return {
                accent: '#fbbf24',
                border: 'rgba(251, 191, 36, 0.6)',
                gradient: 'linear-gradient(135deg, rgba(251, 191, 36, 0.24), rgba(251, 191, 36, 0.08))',
                shadow: 'rgba(251, 191, 36, 0.18)',
                label: 'On track'
            };
        }

        if (parsedScore >= 4) {
            return {
                accent: '#f97316',
                border: 'rgba(249, 115, 22, 0.6)',
                gradient: 'linear-gradient(135deg, rgba(249, 115, 22, 0.22), rgba(249, 115, 22, 0.08))',
                shadow: 'rgba(249, 115, 22, 0.18)',
                label: 'Needs improvement'
            };
        }

        return {
            accent: '#f87171',
            border: 'rgba(248, 113, 113, 0.6)',
            gradient: 'linear-gradient(135deg, rgba(248, 113, 113, 0.22), rgba(248, 113, 113, 0.08))',
            shadow: 'rgba(248, 113, 113, 0.2)',
            label: 'Critical attention'
        };
    }, [parsedScore]);

    // Customer fit visuals
    const customerFitVisuals = useMemo(() => {
        if (!customerFitData.customerFitRating) {
            return {
                accent: '#60a5fa',
                border: 'rgba(96, 165, 250, 0.45)',
                gradient: 'linear-gradient(135deg, rgba(96, 165, 250, 0.18), rgba(96, 165, 250, 0.06))',
                shadow: 'rgba(37, 99, 235, 0.18)',
                label: 'Awaiting signal'
            };
        }

        const normalizedValue = String(customerFitData.customerFitRating).toLowerCase();
        const numericValue = Number(customerFitData.customerFitRating);

        if (Number.isFinite(numericValue)) {
            if (numericValue >= 8) {
                return {
                    accent: '#34d399',
                    border: 'rgba(52, 211, 153, 0.65)',
                    gradient: 'linear-gradient(135deg, rgba(52, 211, 153, 0.24), rgba(52, 211, 153, 0.08))',
                    shadow: 'rgba(52, 211, 153, 0.22)',
                    label: 'Strong fit'
                };
            }

            if (numericValue >= 5) {
                return {
                    accent: '#fbbf24',
                    border: 'rgba(251, 191, 36, 0.6)',
                    gradient: 'linear-gradient(135deg, rgba(251, 191, 36, 0.24), rgba(251, 191, 36, 0.08))',
                    shadow: 'rgba(251, 191, 36, 0.18)',
                    label: 'Needs validation'
                };
            }

            return {
                accent: '#f87171',
                border: 'rgba(248, 113, 113, 0.6)',
                gradient: 'linear-gradient(135deg, rgba(248, 113, 113, 0.22), rgba(248, 113, 113, 0.08))',
                shadow: 'rgba(248, 113, 113, 0.2)',
                label: 'Limited fit'
            };
        }

        if (normalizedValue.includes('high') || normalizedValue.includes('strong') || normalizedValue.includes('ideal')) {
            return {
                accent: '#34d399',
                border: 'rgba(52, 211, 153, 0.65)',
                gradient: 'linear-gradient(135deg, rgba(52, 211, 153, 0.24), rgba(52, 211, 153, 0.08))',
                shadow: 'rgba(52, 211, 153, 0.22)',
                label: 'Strong fit'
            };
        }

        if (normalizedValue.includes('medium') || normalizedValue.includes('moderate') || normalizedValue.includes('balanced')) {
            return {
                accent: '#fbbf24',
                border: 'rgba(251, 191, 36, 0.6)',
                gradient: 'linear-gradient(135deg, rgba(251, 191, 36, 0.24), rgba(251, 191, 36, 0.08))',
                shadow: 'rgba(251, 191, 36, 0.18)',
                label: 'Needs validation'
            };
        }

        if (normalizedValue.includes('low') || normalizedValue.includes('weak') || normalizedValue.includes('poor')) {
            return {
                accent: '#f87171',
                border: 'rgba(248, 113, 113, 0.6)',
                gradient: 'linear-gradient(135deg, rgba(248, 113, 113, 0.22), rgba(248, 113, 113, 0.08))',
                shadow: 'rgba(248, 113, 113, 0.2)',
                label: 'Limited fit'
            };
        }

        return {
            accent: '#60a5fa',
            border: 'rgba(96, 165, 250, 0.45)',
            gradient: 'linear-gradient(135deg, rgba(96, 165, 250, 0.18), rgba(96, 165, 250, 0.06))',
            shadow: 'rgba(37, 99, 235, 0.18)',
            label: 'Mixed signals'
        };
    }, [customerFitData.customerFitRating]);

    // Crawl statistics and pages metadata
    const crawlStatistics = website?.crawl_statistics || null;
    const pagesMetadata = website?.pages_metadata || [];
    const websiteGraph = website?.graph_png || null;
    const contacts = website?.contacts || null;
    const prospectEvaluation = website?.prospect_evaluations || null;
    
    // Assets data - from website?.assets (new structure)
    const assets = website?.assets || null;
    const imagesData = assets ? {
        images: assets.images || [],
        total_images: assets.total_images ?? null,
        total_unique_images: assets.total_unique_images ?? null
    } : null;
    
    const videosData = assets ? {
        videos: assets.videos || [],
        total_videos: assets.total_videos ?? null,
        total_unique_videos: assets.total_unique_videos ?? null,
        video_type_stats: assets.video_type_stats || {}
    } : null;
    
    const filesData = assets ? {
        files: assets.files || [],
        total_files: assets.total_files ?? null,
        total_unique_files: assets.total_unique_files ?? null,
        file_category_stats: assets.file_category_stats || {}
    } : null;

    // Forms data - from answers.collect_forms
    const collectForms = answers?.collect_forms;
    const formsData = collectForms?.forms || [];

    // Contact forms data - from answers.has_contact_form
    const hasContactForm = answers?.has_contact_form;
    const contactFormsData = hasContactForm?.forms || (Array.isArray(hasContactForm) ? hasContactForm : []);

    // Social media data - from answers.has_socialmedia
    const socialMediaData = answers?.has_socialmedia || null;

    // Tracking data - extract all tracking-related fields from answers
    const trackingData = useMemo(() => {
        if (!answers) return null;
        
        const trackingFields = {};
        Object.keys(answers).forEach(key => {
            if (key.startsWith('tracking_')) {
                trackingFields[key] = answers[key];
            }
        });
        
        // Return null if no tracking fields found
        return Object.keys(trackingFields).length > 0 ? trackingFields : null;
    }, [answers]);

    // Page features data
    const pageFeatures = website?.page_features || [];

    // Proposed structure data
    const structure = website?.structure || null;
    const navigation = website?.navigation || null;
    const pageAssignments = website?.page_assignments || null;
    const structureGraphPng = website?.structure_graph_png || null;
    const navigationStructure = website?.navigation_structure || null;
    
    // Manual actions data - handle both 'actions' and 'items' structures
    const rawManualActions = website?.manual_actions || null;
    const manualActions = useMemo(() => {
        if (!rawManualActions) return null;
        
        // Handle structure with 'actions' array
        if (rawManualActions.actions && Array.isArray(rawManualActions.actions)) {
            return {
                actions: rawManualActions.actions
            };
        }
        
        // Handle structure with 'items' array (convert to 'actions')
        if (rawManualActions.items && Array.isArray(rawManualActions.items)) {
            return {
                actions: rawManualActions.items
            };
        }
        
        // If it's already an array, wrap it
        if (Array.isArray(rawManualActions)) {
            return {
                actions: rawManualActions
            };
        }
        
        return null;
    }, [rawManualActions]);

    // Concept data - from top level of websiteDetails
    const conceptBlueprint = website?.concept_blueprint || null;
    const proposedPages = website?.proposed_pages || null;

    const designSemantic = website?.design_semantic || website.design_semantic?.result || null;

    const salesPitchContent = website?.sales_pitch_content ?? null;

    return {
        website,
        mainUrl,
        evaluation,
        evaluationMessage,
        reasons,
        networkSummary,
        performanceMetrics,
        parsedScore,
        scoreVisuals,
        normalizedAnswers,
        standardAnswers,
        donationAnswers,
        dnsAnswers,
        seoAnswers,
        longTextAnswers,
        siteTypeData,
        customerFitData,
        customerFitSignals,
        customerFitVisuals,
        gatekeeperData,
        gatekeeperShouldContinue,
        gatekeeperRejected,
        gatekeeperConfidence,
        gatekeeperCategory,
        gatekeeperReason,
        gatekeeperSignals,
        gatekeeperStatus,
        showGatekeeperSummary,
        showTopGatekeeperSummary,
        showBottomGatekeeperStatusCard,
        consoleLogs,
        consoleLogLevelSummary,
        LOG_LEVEL_SUMMARY,
        metricsList,
        navigationTiming,
        resourceEntries,
        coreWebVitals,
        thirdPartySplit,
        blockingScripts,
        coverage,
        defaultMetrics,
        navigationStartValue,
        defaultMetricsDisplay,
        hasAdditionalMetrics,
        initiatorEntries,
        crawlStatistics,
        pagesMetadata,
        websiteGraph,
        contacts,
        prospectEvaluation,
        urlData,
        imagesData,
        videosData,
        filesData,
        formsData,
        contactFormsData,
        socialMediaData,
        trackingData,
        pageFeatures,
        structure,
        navigation,
        pageAssignments,
        structureGraphPng,
        navigationStructure,
        manualActions,
        conceptBlueprint,
        proposedPages,
        cssData,
        designSemantic,
        salesPitchContent
    };
};

